import { Badge, Button, Checkbox, Layout, message, Modal, Space, Spin, Table } from "antd";
import PageHeader from "../../comps/pageHeader";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useCookies } from "react-cookie";
import { useEffect, useState } from "react";
import {
    fetchExercise, PollSession, RemoveUserFromSession,
    setGetExerciseResult, setIsRemovingUser, setPollSessionSuccess,
    setStartSessionSuccess, setStopSessionSuccess, StartSession,
    StopSession
} from "./classroomsSlice";
import { secondsToTimeFormat } from "../../lib/formatting";
import Parser from 'html-react-parser';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import {
    ExclamationCircleTwoTone
} from '@ant-design/icons'

export default function LessonSession() {
    let { id } = useParams();
    const intervalTimeMS = 1;

    const [classId, lessonId] = id.split("-");

    // eslint-disable-next-line no-unused-vars
    const [cookies, setCookies, removeCookie] = useCookies();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [sessionStarted, setSessionStarted] = useState(false);
    const [sessionFinished, setSessionFinished] = useState(false);
    const [sessionData, setSessionData] = useState(null);
    const [localTimeSpent, setLocalTimeSpent] = useState(0);
    const [lastPoll, setLastPoll] = useState(0);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [quitModalOpen, setQuitModalOpen] = useState(false);
    const [singleUserModalOpen, setSingleUserModalOpen] = useState(false);
    const [selectedUserSession, setSelectedUserSession] = useState(null);
    const [singleUserCompoundData, setSingleUserCompoundData] = useState(null);
    const [shouldRemoveUser, setShouldRemoveUser] = useState(false);
    const [checkBoxState, setCheckboxState] = useState([]);

    const classroomSingle = useSelector((state) => state.profs.classroomSingle);
    const startSessionResult = useSelector((state) => state.classrooms.startSessionResult);
    const isStartingSession = useSelector((state) => state.classrooms.isStartingSession);
    const pollSessionResult = useSelector((state) => state.classrooms.pollSessionResult);
    const isPollingSession = useSelector((state) => state.classrooms.isPollingSession);
    const stopSessionResult = useSelector((state) => state.classrooms.stopSessionResult);
    const isStoppingSession = useSelector((state) => state.classrooms.isStoppingSession);
    const exerciseResult = useSelector((state) => state.classrooms.getExerciseResult);
    const removeUserResult = useSelector((state) => state.classrooms.removeUserResult);
    const isRemovingUser = useSelector((state) => state.classrooms.isRemovingUser);

    useEffect(() => {
        dispatch(StartSession(cookies, { classId, lessonId }));

        return () => {
            dispatch(setStartSessionSuccess(null));
            dispatch(setPollSessionSuccess(null));
            dispatch(setStopSessionSuccess(null));

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!startSessionResult || sessionStarted) {
            return;
        }
        setSessionStarted(true);
        setSessionData(startSessionResult.classSession);
        setLocalTimeSpent(startSessionResult.classSession?.timeSpent);
        setCheckboxState(Array.from({ length: startSessionResult.classSession.sessionUserExercises.length }, () => false))

        const interval = setInterval(() => {
            if (sessionFinished) {
                return clearInterval(interval);
            }
            setLocalTimeSpent((prevTime) => prevTime + intervalTimeMS);
        }, intervalTimeMS * 1000);
        setLastPoll(new Date());
        if (!isPollingSession) {

            dispatch(PollSession(cookies, { sessionId: startSessionResult.classSession._id, secondsToAdd: intervalTimeMS }));
        }

        return () => clearInterval(interval)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startSessionResult]);

    useEffect(() => {
        if (!pollSessionResult || !sessionStarted) return;

        setSessionData(pollSessionResult.classSession);

        if (selectedUserSession) {
            const newData = pollSessionResult.classSession.sessionUserExercises;
            const newSUS = newData.filter((SUE) => SUE.pupil._id === selectedUserSession.pupil._id)[0];
            setSelectedUserSession(newSUS);
            console.log(newSUS);

            if (exerciseResult) {
                const tableData = CreateSingleTableData(newSUS, exerciseResult);
                setSingleUserCompoundData(tableData);
            }
        }

        if (shouldRemoveUser) {
            if (selectedUsers.length !== 0 && !isRemovingUser) {
                dispatch(RemoveUserFromSession(cookies, { sessionId: sessionData._id, pupilIds: selectedUsers }));
                return;
            }
            setIsRemovingUser(false);
            message.warning("Aucun utilisateur sélectionné");
        }

        const now = new Date();
        const diffInSeconds = (now - lastPoll) / 1000;
        if (sessionFinished) {
            dispatch(StopSession(cookies, { sessionId: sessionData._id, secondsToAdd: 0 }));
            return;
        }
        if (!isPollingSession && !isStoppingSession && !sessionFinished) {
            dispatch(PollSession(cookies, { sessionId: sessionData._id, secondsToAdd: diffInSeconds }));
        }

        setLastPoll(now);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pollSessionResult]);

    useEffect(() => {
        if (!removeUserResult || !sessionStarted) {
            return;
        }

        clearCheckedUsers();
        setShouldRemoveUser(false);

        const now = new Date();
        const diffInSeconds = (now - lastPoll) / 1000;
        dispatch(PollSession(cookies, { sessionId: sessionData._id, secondsToAdd: diffInSeconds }));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removeUserResult])

    useEffect(() => {
        if (!stopSessionResult) return;
        setSessionFinished(true);
        dispatch(setStopSessionSuccess(null));
        navigate(`/dashboard/classes/class/${classId}`);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stopSessionResult]);

    useEffect(() => {
        if (!selectedUserSession) return;
        dispatch(fetchExercise(cookies, { exoId: selectedUserSession.userExercise.exercise }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUserSession])

    useEffect(() => {
        if (!exerciseResult) return;
        const tableData = CreateSingleTableData(selectedUserSession, exerciseResult);
        setSingleUserCompoundData(tableData)
        setSingleUserModalOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exerciseResult])

    const handleStopSession = () => {
        setSessionFinished(true);
    }

    const handleQuitModalClicked = () => {
        setQuitModalOpen(true);
    }

    const handleSelectUserExo = (exists, record) => {
        if (!exists) {
            message.warning("Cet élève n'est pas en train de faire un exercice");
            return;
        }
        setSelectedUserSession(record);
    }

    const addOrRemoveCheckedUser = (checked, id) => {
        if (checked) {
            setSelectedUsers([...selectedUsers, id]);
        }
        else {
            setSelectedUsers(selectedUsers.filter(localId => localId !== id));
        }
    }

    const exportToExcel = (data, filename) => {
        const newData = ReformatUserSessionDataForXLSX(data);
        const worksheet = XLSX.utils.json_to_sheet(newData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Données Session');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, `${filename}.xlsx`);
    };

    // eslint-disable-next-line no-unused-vars
    const clearCheckedUsers = () => {
        setSelectedUsers([]);
        const replacementArray = [];
        for (let index = 0; index < checkBoxState.length; index++) {
            replacementArray.push(false);
        }
        setCheckboxState(replacementArray);
    }

    const columnsSession = [
        {
            title: '',
            dataIndex: 'pupil',
            key: '_id',
            render: (pupil, record, index) => (<Checkbox checked={checkBoxState[index]} onChange={(e) => {
                checkBoxState[index] = e.target.checked;
                addOrRemoveCheckedUser(e.target.checked, pupil?._id);
            }}></Checkbox>),
            width: "48px"
        },
        {
            title: 'Nom prénom',
            dataIndex: 'pupil',
            key: '_id',
            render: (pupil) => (<p>{pupil?.name}</p>),
            width: "400px",
            // ...getColumnSearchProps('code')
        },
        {
            title: 'Statut',
            dataIndex: 'pupil',
            key: '_id',
            render: (pupil) => (<p>{
                (new Date() - new Date(pupil?.lastSeen)) / 1000 < 180 ? "En ligne" : "Hors Ligne"
            }</p>),
            // width: "600px",
            // ...getColumnSearchProps('code')
        },
        {
            title: 'Leçon',
            dataIndex: 'lesson',
            key: '_id',
            render: (lesson) => (<p>{lesson ? lesson.name : "Aucun exo"}</p>),
            // width: "600px",
            // ...getColumnSearchProps('code')
        },
        {
            title: 'Progression',
            dataIndex: 'userExercise',
            key: '_id',
            render: (exo) => (<p>{exo ? Math.floor(exo.answers.length / 12 * 100) + "%" : "Aucun exo"}</p>),
        },
        {
            title: 'Nbr. d\'Erreurs',
            dataIndex: 'userExercise',
            key: '_id',
            render: (exo) => (<p>{exo ? exo.answers.reduce((acc, num) => acc + (num === 0 ? 1 : 0), 0) : "Aucun exo"}</p>),
        },
        {
            title: 'Détails',
            dataIndex: 'userExercise',
            key: '_id',
            render: (data, record) => (
                <div >
                    <Button
                        style={{ width: "6vw", marginRight: "8px", color: "#39B13D", border: "none" }}
                        onClick={() => { handleSelectUserExo(data, record) }}
                    >
                        Voir
                    </Button>
                </div>
            ),
            width: "10vw"
            // ...getColumnFilterStatusProps('sent')
        }

    ];

    const columnsSingleSession = [
        {
            title: 'Question',
            dataIndex: 'Question',
            key: '_id',
            render: (content) => (<p>{content}</p>),
        },
        {
            title: 'Bonne réponse',
            dataIndex: 'Bonne réponse',
            key: '_id',
            render: (correction) => (<p>{correction}</p>),
        },
        {
            title: 'Réponse Étudiant',
            dataIndex: 'Réponse Étudiant',
            key: '_id',
            render: (answer) => (<div style={{ display: "flex", flexDirection: "row" }}>
                <Badge dot={true} color={answer === 0 ? "#FAAD14" : "#52C41A"} />
                <p style={{ marginLeft: "4px" }}>{answer === 0 ? "Faux" : "Juste"}</p>
            </div>),
        },
    ]

    return (
        <Space direction="vertical" style={{ width: '100%' }} size={[0, 48]}>
            <Layout>
                <PageHeader title={`${classroomSingle?.classroom.name} ${!startSessionResult ? "" : startSessionResult.classSession.lesson.name}`} breadcrumbItems={["Vue d'ensemble", "Session"]}>
                    <Button
                        className="buttonStyle"
                        style={{ marginTop: "20px", height: "32px", padding: "4px 24px" }}
                        onClick={() => { navigate("/dashboard/classes/classList"); }}
                    >Revenir à la liste des classes </Button>
                </PageHeader>
                <div style={{
                    margin: "2em",
                    padding: "2em 4vw 2em 4vw",
                    display: "flex",
                    direction: "column",
                    justifyContent: "space-around",
                    backgroundColor: "#424242"
                }}>
                    <div className="buttonContainer">
                        <p style={{ color: "#fff", fontWeight: "600" }}>Temps</p>
                        <Button
                            className="buttonStyle"
                        // onClick={() => message.warning("Pas encore implémenté")}
                        >
                            {isStartingSession ? <Spin /> : secondsToTimeFormat(localTimeSpent)}
                        </Button>
                    </div>
                    <div className="buttonContainer">
                        <p style={{ color: "#fff", fontWeight: "600" }}>Supprimer un élève :</p>
                        <Button
                            className="buttonStyle"
                            onClick={() => {
                                setShouldRemoveUser(true);
                            }}
                        >
                            {shouldRemoveUser ? <Spin /> : "Supprimer"}
                        </Button>
                    </div>
                    <div className="buttonContainer">
                        <p style={{ color: "#fff", fontWeight: "600" }}>Quitter la leçon</p>
                        <Button
                            className="buttonStyle"
                            onClick={handleQuitModalClicked}
                        >
                            {"Quitter"}
                        </Button>
                    </div>
                </div>
                <div
                    style={{
                        margin: "2em",
                        padding: "1em 2em",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        backgroundColor: "#Fff"
                    }}
                >
                    <div style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between"
                    }}>
                        <h3>Liste des élèves de la classe {classroomSingle?.classroom.name}</h3>
                    </div>
                    <div style={{ padding: "12px 12px" }}>
                        <Table
                            loading={isStartingSession}
                            dataSource={!sessionData ? [] : sessionData.sessionUserExercises}
                            columns={columnsSession}
                        // onChange={onChangePage}
                        />
                    </div>
                </div>

                <Modal
                    title={<div style={{ display: "flex", flexDirection: "row" }}>
                        <ExclamationCircleTwoTone twoToneColor="#FAAD14" style={{ marginRight: "8px" }} />
                        <p>Attention !</p>
                    </div>}
                    open={quitModalOpen}
                    cancelText="Non"
                    onCancel={() => { setQuitModalOpen(false) }}
                    okText={sessionFinished ? <Spin /> : "Oui"}
                    onOk={handleStopSession}
                >
                    <p style={{ marginLeft: "16px" }}>Voulez-vous vraiment quitter cette leçon ?</p>
                </Modal>

                <Modal
                    title={selectedUserSession ? selectedUserSession.pupil.name : ""}
                    open={singleUserModalOpen}
                    cancelText="Fermer"
                    onCancel={() => {
                        setSingleUserModalOpen(false);
                        dispatch(setGetExerciseResult(null));
                        setSelectedUserSession(null);
                    }}
                    onOk={() => { exportToExcel(singleUserCompoundData, selectedUserSession.pupil.name + "_Session") }}
                    okText="Exporter"
                    width={"800px"}
                >
                    <p style={{ fontSize: "16px", marginTop: "8px", marginBottom: "16px" }}><b>Enoncé:</b> {Parser(exerciseResult ? exerciseResult.exercise?.statement : "")}</p>

                    <Table
                        dataSource={singleUserCompoundData}
                        columns={columnsSingleSession}
                    />
                </Modal>
            </Layout>

        </Space>
    );
}

function ReformatUserSessionDataForXLSX(data) {
    const tableData = []
    for (let i = 0; i < data.length; i++) {
        const element = data[i];
        const exoData = {
            'Réponse Étudiant': element['Réponse Étudiant'] === 0 ? "Faux" : "Juste",
            'Bonne réponse': element['Bonne réponse'],
            'Question': element['Question'],
        };
        tableData.push(exoData);
    }

    return tableData;
}
function CreateSingleTableData(selectedUserSession, exerciseResult) {
    const tableData = [];
    for (let i = 0; i < selectedUserSession?.userExercise?.answers.length; i++) {
        const answer = selectedUserSession?.userExercise?.answers[i];

        const exoData = {
            'Réponse Étudiant': answer,
            'Bonne réponse': exerciseResult.exercise.questions[i].correction.content,
            'Question': exerciseResult.exercise.questions[i].content.join(" ")
        };
        tableData.push(exoData);
    }
    return tableData;
}

